import {
  EMPLOYEE_OPERATOR_VISIBLE_FIELDS,
  type EmployeeOperatorView,
  isEmployeeOperatorEditableField,
} from "@shared/constants/employee-operator";
import { getOrgTeamLabel, ORG_TEAMS, teamForJobTitle } from "@shared/constants/org-teams";
import type { Employee } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { ColumnManagerSheet } from "@/components/employees/column-manager-sheet";
import { DataUploadDialog } from "@/components/employees/data-upload-dialog";
import { EmergencyContactDialog } from "@/components/employees/emergency-contact-dialog";
import { SlackSyncModal } from "@/components/employees/slack-sync-modal";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { useEmployeeAccess } from "@/hooks/use-employee-access";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSplitView } from "@/hooks/use-split-view";
import { useToast } from "@/hooks/use-toast";
import {
  COMP_DISCIPLINES,
  DEPARTMENTS,
  EMPLOYEE_CURRENCIES,
  EMPLOYEE_STATUSES,
  EMPLOYMENT_TYPES,
  formatCurrency,
  formatDate,
  getCurrencyLabel,
  JOB_LEVELS,
  WORK_TYPES,
} from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc } from "@/lib/rpc";
import EmployeeDetails from "@/pages/employee-details";

type FullEmployeeRow = Employee & { directManagerName?: string };
/**
 * A grid row: the full record, or — for an Operador de Colaboradores — the
 * server's projection, whose compensation/personal keys are absent.
 */
type EmployeeWithManagerName = FullEmployeeRow | EmployeeOperatorView<FullEmployeeRow>;

// Word-Carries-State for employee status (DESIGN v7 §4) — palavra em body-strong + cor canônica, sem container
function EmployeeStatus({ value }: { value: string | null | undefined }) {
  const label = EMPLOYEE_STATUSES.find((s) => s.value === value)?.label || value || "-";
  const color = (() => {
    switch (value) {
      case "active":
        return "text-[hsl(var(--forest-pulse))]";
      case "onboarding":
        return "text-[hsl(var(--amber-state))]";
      case "inactive":
      case "terminated":
      case "archived":
        return "text-[hsl(var(--ink-muted))]";
      case "pending_action":
      case "pending_approval":
      case "waiting_approval":
        return "text-[hsl(var(--comp-coral))]";
      default:
        return "text-[hsl(var(--ink-muted))]";
    }
  })();
  return <span className={`body-strong ${color}`}>{label}</span>;
}

function nowHHmm() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

type TextOp =
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "equals"
  | "is_empty"
  | "not_empty";

type NumberOp = "eq" | "neq" | "gt" | "lt" | "gte" | "lte" | "between" | "is_empty" | "not_empty";

type DateOp = "before" | "after" | "on" | "between" | "is_empty" | "not_empty";

type TextFilterValue = { kind: "text"; op: TextOp; value: string };
type NumberFilterValue = {
  kind: "number";
  op: NumberOp;
  value: number | null;
  value2?: number | null;
};
type DateFilterValue = {
  kind: "date";
  op: DateOp;
  value: string | null;
  value2?: string | null;
};

type ColumnFilterValue = string[] | TextFilterValue | NumberFilterValue | DateFilterValue;

type OpMeta<T> = { value: T; label: string; short: string; needsValue: 0 | 1 | 2 };

const TEXT_OPS: OpMeta<TextOp>[] = [
  { value: "contains", label: "Contém", short: "contém", needsValue: 1 },
  { value: "not_contains", label: "Não contém", short: "não contém", needsValue: 1 },
  { value: "starts_with", label: "Começa com", short: "começa com", needsValue: 1 },
  { value: "ends_with", label: "Termina com", short: "termina com", needsValue: 1 },
  { value: "equals", label: "É igual a", short: "=", needsValue: 1 },
  { value: "is_empty", label: "Está vazio", short: "está vazio", needsValue: 0 },
  { value: "not_empty", label: "Não está vazio", short: "não está vazio", needsValue: 0 },
];

const NUMBER_OPS: OpMeta<NumberOp>[] = [
  { value: "eq", label: "É igual a", short: "=", needsValue: 1 },
  { value: "neq", label: "É diferente de", short: "≠", needsValue: 1 },
  { value: "gt", label: "Maior que", short: ">", needsValue: 1 },
  { value: "lt", label: "Menor que", short: "<", needsValue: 1 },
  { value: "gte", label: "Maior ou igual a", short: "≥", needsValue: 1 },
  { value: "lte", label: "Menor ou igual a", short: "≤", needsValue: 1 },
  { value: "between", label: "Entre", short: "entre", needsValue: 2 },
  { value: "is_empty", label: "Está vazio", short: "está vazio", needsValue: 0 },
  { value: "not_empty", label: "Não está vazio", short: "não está vazio", needsValue: 0 },
];

const DATE_OPS: OpMeta<DateOp>[] = [
  { value: "on", label: "Em", short: "em", needsValue: 1 },
  { value: "before", label: "Antes de", short: "antes de", needsValue: 1 },
  { value: "after", label: "Depois de", short: "depois de", needsValue: 1 },
  { value: "between", label: "Entre", short: "entre", needsValue: 2 },
  { value: "is_empty", label: "Está vazio", short: "está vazio", needsValue: 0 },
  { value: "not_empty", label: "Não está vazio", short: "não está vazio", needsValue: 0 },
];

function isTextFilter(v: unknown): v is TextFilterValue {
  return !!v && typeof v === "object" && (v as { kind?: string }).kind === "text";
}
function isNumberFilter(v: unknown): v is NumberFilterValue {
  return !!v && typeof v === "object" && (v as { kind?: string }).kind === "number";
}
function isDateFilter(v: unknown): v is DateFilterValue {
  return !!v && typeof v === "object" && (v as { kind?: string }).kind === "date";
}

function toNumberOrNull(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function toDateOrNull(raw: unknown): string | null {
  if (raw === null || raw === undefined || raw === "") return null;
  const s = String(raw);
  // accept ISO-ish; strip time
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

const dispatchColumnFilter = (
  row: { getValue: (columnId: string) => unknown },
  columnId: string,
  filterValue: ColumnFilterValue,
) => {
  if (Array.isArray(filterValue)) {
    if (filterValue.length === 0) return true;
    const cellValue = row.getValue(columnId) as string | null | undefined;
    return filterValue.includes(cellValue ?? "");
  }
  if (isTextFilter(filterValue)) {
    const raw = row.getValue(columnId);
    const cell = String(raw ?? "").toLowerCase();
    const v = (filterValue.value ?? "").toLowerCase();
    switch (filterValue.op) {
      case "is_empty":
        return cell === "";
      case "not_empty":
        return cell !== "";
      case "contains":
        return v === "" || cell.includes(v);
      case "not_contains":
        return v === "" || !cell.includes(v);
      case "starts_with":
        return v === "" || cell.startsWith(v);
      case "ends_with":
        return v === "" || cell.endsWith(v);
      case "equals":
        return v === "" || cell === v;
    }
  }
  if (isNumberFilter(filterValue)) {
    const cell = toNumberOrNull(row.getValue(columnId));
    const v = filterValue.value;
    const v2 = filterValue.value2 ?? null;
    switch (filterValue.op) {
      case "is_empty":
        return cell === null;
      case "not_empty":
        return cell !== null;
    }
    if (cell === null) return false;
    switch (filterValue.op) {
      case "eq":
        return v === null || cell === v;
      case "neq":
        return v === null || cell !== v;
      case "gt":
        return v === null || cell > v;
      case "lt":
        return v === null || cell < v;
      case "gte":
        return v === null || cell >= v;
      case "lte":
        return v === null || cell <= v;
      case "between": {
        if (v === null && v2 === null) return true;
        const lo = v ?? Number.NEGATIVE_INFINITY;
        const hi = v2 ?? Number.POSITIVE_INFINITY;
        return cell >= Math.min(lo, hi) && cell <= Math.max(lo, hi);
      }
    }
  }
  if (isDateFilter(filterValue)) {
    const cell = toDateOrNull(row.getValue(columnId));
    const v = filterValue.value;
    const v2 = filterValue.value2 ?? null;
    switch (filterValue.op) {
      case "is_empty":
        return cell === null;
      case "not_empty":
        return cell !== null;
    }
    if (cell === null) return false;
    switch (filterValue.op) {
      case "on":
        return !v || cell === v;
      case "before":
        return !v || cell < v;
      case "after":
        return !v || cell > v;
      case "between": {
        if (!v && !v2) return true;
        const lo = v ?? "0000-01-01";
        const hi = v2 ?? "9999-12-31";
        const min = lo < hi ? lo : hi;
        const max = lo < hi ? hi : lo;
        return cell >= min && cell <= max;
      }
    }
  }
  return true;
};

type FilterableColumnConfig =
  | {
      kind: "enum";
      columnId: string;
      label: string;
      options: readonly { value: string; label: string }[] | { value: string; label: string }[];
    }
  | { kind: "text"; columnId: string; label: string }
  | { kind: "number"; columnId: string; label: string; isCurrency?: boolean }
  | { kind: "date"; columnId: string; label: string };

// Inline edit cell component
function InlineEditCell({
  value,
  onSave,
  onCancel,
  type = "text",
  placeholder,
}: {
  value: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  type?: string;
  placeholder?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [editValue, setEditValue] = useState(value ?? "");
  const cancellingRef = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleBlur = () => {
    if (cancellingRef.current) {
      cancellingRef.current = false;
      onCancel();
      return;
    }
    onSave(editValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSave(editValue);
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancellingRef.current = true;
      inputRef.current?.blur();
    }
  };

  return (
    <input
      ref={inputRef}
      type={type}
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className="w-full bg-transparent border-none outline-none text-sm h-full p-0 focus:ring-0"
    />
  );
}

/** Columns an Operador de Colaboradores sees (the server projection + row selection). */
const OPERATOR_COLUMN_IDS = new Set<string>([
  ...EMPLOYEE_OPERATOR_VISIBLE_FIELDS,
  "select",
  "team", // derived from jobTitle, which the operator sees
]);

/**
 * For an operator, keeps only the columns/filters its rows actually carry.
 * Takes the item type explicitly so the inline array literals stay typed.
 */
function keepOperatorColumns<T>(isOperator: boolean, idOf: (item: T) => string, items: T[]): T[] {
  return isOperator ? items.filter((item) => OPERATOR_COLUMN_IDS.has(idOf(item))) : items;
}

function columnIdOf(col: ColumnDef<EmployeeWithManagerName>): string {
  if ("accessorKey" in col && col.accessorKey) return String(col.accessorKey);
  return col.id ?? "";
}

/** Spreadsheet columns exported for an operator — the ones its rows carry. */
const OPERATOR_EXPORT_COLUMNS = [
  "Nome",
  "Cargo",
  "Time",
  "Disciplina Comp",
  "Nivel",
  "Departamento",
  "Gestor",
  "Status",
  "Data Inicio",
  "Data Fim",
  "Email Corporativo",
] as const;

export default function Employees() {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { isSuperAdmin, isManager, hasPermission } = useAuth();
  // General employee management is gated by the `employees` permission (admins
  // pass via hasPermission). The Operador de Colaboradores (`employees_manager`)
  // reaches the whole base too, but the server hands it a projection without
  // compensation or personal data — so those columns, filters and actions are
  // hidden here, and only the operator fields are editable.
  const { canManageEmployees, canMaintainEmployeeBase, isEmployeeOperator } = useEmployeeAccess();
  // Compa Ratio and Tabela Salarial are derived from the salary-tables product,
  // so they follow that product's own permission — holding `employees` grants
  // the colaborador's own data (including salary), not another product's.
  // Permissions compose: `employees` + `salary_tables` sees both. Mirrors the
  // server rule on those reads, so we never fire a request that 403s and trips
  // the global "Sem permissão" toast.
  const canSeeSalaryTables = hasPermission("salary_tables") || isManager;
  const [slackSyncOpen, setSlackSyncOpen] = useState(false);
  const [dataUploadOpen, setDataUploadOpen] = useState(false);
  const [addEmployeeOpen, setAddEmployeeOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    fullName: "",
    corporateEmail: "",
    slackId: "",
    directManagerId: "",
  });
  const [emergencyContactOpen, setEmergencyContactOpen] = useState(false);
  const [selectedEmployeeForContact, setSelectedEmployeeForContact] =
    useState<EmployeeWithManagerName | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<EmployeeWithManagerName | null>(null);
  const { openSplitView } = useSplitView();

  // Table state
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: "status", value: ["active", "onboarding"] },
  ]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnManagerOpen, setColumnManagerOpen] = useState(false);
  const [editingCell, setEditingCell] = useState<{ rowId: string; columnId: string } | null>(null);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [valuesVisible, setValuesVisible] = useState(false);
  const salaryVisible = valuesVisible;
  const sopVisible = valuesVisible;

  // Canonical table v2 — inline expansível search + keyboard shortcuts
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const lastClickedCellRef = useRef<{ rowId: string; columnId: string } | null>(null);

  // Density (Alt+H+O+I/R/H — Excel-style sequence)
  type Density = "compact" | "comfortable" | "relaxed";
  const [density, setDensity] = useState<Density>("comfortable");
  const densityHeights: Record<Density, number> = {
    compact: 36,
    comfortable: 44,
    relaxed: 56,
  };
  const rowHeight = densityHeights[density];
  const [altSeq, setAltSeq] = useState<string[]>([]);

  // Fetch employees
  const { data: employees = [], isLoading } = useQuery<EmployeeWithManagerName[]>(
    orpc.employees.list.queryOptions(),
  );

  const { data: salaryTables = [] } = useQuery({
    ...orpc.salaryTables.listTables.queryOptions(),
    enabled: canSeeSalaryTables,
  });

  const { data: allBands = [] } = useQuery({
    ...orpc.salaryTables.listAllBands.queryOptions(),
    enabled: canSeeSalaryTables,
  });

  const bandLookup = useMemo(() => {
    const map = new Map<string, number>();
    for (const band of allBands) {
      map.set(`${band.salaryTableId}::${band.key}`, band.midValue);
    }
    return map;
  }, [allBands]);

  const updateFieldMutation = useMutation({
    ...orpc.employees.updateField.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.employees.list.queryOptions().queryKey });
      toast({
        description: `Salvo às ${nowHHmm()}.`,
      });
    },
  });

  // Undo stack (Cmd/Ctrl+Z) — last 50 inline cell edits.
  type UndoEntry = {
    employeeId: string;
    employeeName: string;
    field: string;
    fieldLabel: string;
    prevValue: any;
    newValue: any;
  };
  const undoStackRef = useRef<UndoEntry[]>([]);
  const isUndoingRef = useRef(false);

  const fieldLabel = useCallback((field: string): string => {
    const map: Record<string, string> = {
      fullName: "Nome",
      corporateEmail: "E-mail corporativo",
      personalEmail: "E-mail pessoal",
      jobTitle: "Cargo",
      compDiscipline: "Disciplina Comp",
      jobLevel: "Nível",
      department: "Departamento",
      directManagerId: "Gestor",
      status: "Status",
      employmentType: "Tipo de contrato",
      workType: "Modelo de trabalho",
      startDate: "Data início",
      endDate: "Data fim",
      phone: "Telefone",
      cpf: "CPF",
      birthdate: "Data nascimento",
      slackId: "Slack ID",
      address: "Endereço",
      currentSalary: "Salário",
      currency: "Moeda",
      sop: "SOP",
      salaryTableId: "Tabela salarial",
    };
    return map[field] ?? field;
  }, []);

  const handleSaveField = useCallback(
    (employee: EmployeeWithManagerName, field: string, newValue: any) => {
      const originalValue = (employee as any)[field];
      const normalizedNew = newValue === "" ? null : newValue;
      const normalizedOrig = originalValue === "" ? null : originalValue;
      if (normalizedNew === normalizedOrig) return;
      if (String(normalizedNew ?? "") === String(normalizedOrig ?? "")) return;

      // Push to undo stack only when it's a forward edit (not the undo itself).
      if (!isUndoingRef.current) {
        undoStackRef.current.push({
          employeeId: employee.id,
          employeeName: employee.fullName ?? "Colaborador",
          field,
          fieldLabel: fieldLabel(field),
          prevValue: originalValue,
          newValue,
        });
        if (undoStackRef.current.length > 50) undoStackRef.current.shift();
      }

      updateFieldMutation.mutate({ id: employee.id, field, value: newValue });
    },
    [updateFieldMutation, fieldLabel],
  );

  const performUndo = useCallback(() => {
    const entry = undoStackRef.current.pop();
    if (!entry) {
      toast({ description: "Nada pra desfazer." });
      return;
    }
    isUndoingRef.current = true;
    updateFieldMutation.mutate(
      { id: entry.employeeId, field: entry.field, value: entry.prevValue },
      {
        onSettled: () => {
          isUndoingRef.current = false;
        },
      },
    );
    toast({
      description: `Desfeito: ${entry.fieldLabel} de ${entry.employeeName}.`,
    });
  }, [updateFieldMutation, toast]);

  const deleteEmployeeMutation = useMutation({
    ...orpc.employees.delete.mutationOptions(),
    onSuccess: () => {
      const name = employeeToDelete?.fullName ?? "Colaborador";
      queryClient.invalidateQueries({ queryKey: orpc.employees.list.queryOptions().queryKey });
      toast({ description: `${name} removido.` });
      setDeleteConfirmOpen(false);
      setEmployeeToDelete(null);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        description: error.message || "Falha ao remover colaborador. Tente novamente.",
      });
    },
  });

  const createEmployeeMutation = useMutation({
    ...orpc.employees.create.mutationOptions(),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: orpc.employees.list.queryOptions().queryKey });
      const name = data?.fullName ?? newEmployee.fullName;
      toast({ description: `Colaborador cadastrado: ${name}.` });
      setAddEmployeeOpen(false);
      setNewEmployee({ fullName: "", corporateEmail: "", slackId: "", directManagerId: "" });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        description: error.message || "Falha ao cadastrar colaborador. Tente novamente.",
      });
    },
  });

  // Column definitions for select fields
  type SelectColumnConfig = {
    field: string;
    options: readonly { value: string; label: string }[] | { value: string; label: string }[];
    placeholder: string;
    testIdPrefix: string;
  };

  const selectColumns: Record<string, SelectColumnConfig> = useMemo(
    () => ({
      compDiscipline: {
        field: "compDiscipline",
        options: COMP_DISCIPLINES,
        placeholder: "Selecione a disciplina",
        testIdPrefix: "select-comp-discipline",
      },
      jobLevel: {
        field: "jobLevel",
        options: JOB_LEVELS,
        placeholder: "Selecione o nivel",
        testIdPrefix: "select-job-level",
      },
      department: {
        field: "department",
        options: DEPARTMENTS,
        placeholder: "Selecione o departamento",
        testIdPrefix: "select-department",
      },
      status: {
        field: "status",
        options: EMPLOYEE_STATUSES,
        placeholder: "Selecione o status",
        testIdPrefix: "select-status",
      },
      employmentType: {
        field: "employmentType",
        options: EMPLOYMENT_TYPES,
        placeholder: "Selecione o tipo",
        testIdPrefix: "select-employment-type",
      },
      workType: {
        field: "workType",
        options: WORK_TYPES,
        placeholder: "Selecione o modelo",
        testIdPrefix: "select-work-type",
      },
      currency: {
        field: "currency",
        options: EMPLOYEE_CURRENCIES.map((c) => ({ value: c.value, label: c.label })),
        placeholder: "Selecione a moeda",
        testIdPrefix: "select-currency",
      },
    }),
    [],
  );

  const filterableColumns: FilterableColumnConfig[] = useMemo(
    () =>
      keepOperatorColumns<FilterableColumnConfig>(isEmployeeOperator, (def) => def.columnId, [
        { kind: "enum", columnId: "status", label: "Status", options: EMPLOYEE_STATUSES },
        { kind: "enum", columnId: "department", label: "Departamento", options: DEPARTMENTS },
        { kind: "enum", columnId: "jobLevel", label: "Nível", options: JOB_LEVELS },
        {
          kind: "enum",
          columnId: "compDiscipline",
          label: "Disciplina Comp",
          options: COMP_DISCIPLINES,
        },
        {
          kind: "enum",
          columnId: "employmentType",
          label: "Tipo Contrato",
          options: EMPLOYMENT_TYPES,
        },
        { kind: "enum", columnId: "workType", label: "Modelo Trabalho", options: WORK_TYPES },
        { kind: "text", columnId: "fullName", label: "Nome" },
        { kind: "text", columnId: "jobTitle", label: "Cargo" },
        { kind: "enum", columnId: "team", label: "Time", options: ORG_TEAMS },
        { kind: "text", columnId: "corporateEmail", label: "Email Corporativo" },
        { kind: "text", columnId: "personalEmail", label: "Email Pessoal" },
        { kind: "text", columnId: "phone", label: "Telefone" },
        { kind: "text", columnId: "cpf", label: "CPF" },
        { kind: "text", columnId: "address", label: "Endereco" },
        { kind: "text", columnId: "slackId", label: "Slack ID" },
        { kind: "number", columnId: "currentSalary", label: "Salário", isCurrency: true },
        {
          kind: "enum",
          columnId: "currency",
          label: "Moeda",
          options: EMPLOYEE_CURRENCIES.map((c) => ({ value: c.value, label: c.label })),
        },
        ...(canSeeSalaryTables
          ? [{ kind: "number" as const, columnId: "compaRatio", label: "Compa Ratio" }]
          : []),
        { kind: "number", columnId: "sop", label: "SOP" },
        { kind: "date", columnId: "startDate", label: "Data Início" },
        { kind: "date", columnId: "endDate", label: "Data Fim" },
        { kind: "date", columnId: "birthdate", label: "Data Nascimento" },
      ]),
    [canSeeSalaryTables, isEmployeeOperator],
  );

  const activeFilterCount = useMemo(
    () =>
      columnFilters.filter((f) => {
        if (Array.isArray(f.value)) return f.value.length > 0;
        if (isTextFilter(f.value)) {
          const op = f.value.op;
          if (op === "is_empty" || op === "not_empty") return true;
          return (f.value.value ?? "").trim().length > 0;
        }
        if (isNumberFilter(f.value)) {
          const op = f.value.op;
          if (op === "is_empty" || op === "not_empty") return true;
          if (op === "between") return f.value.value !== null || (f.value.value2 ?? null) !== null;
          return f.value.value !== null;
        }
        if (isDateFilter(f.value)) {
          const op = f.value.op;
          if (op === "is_empty" || op === "not_empty") return true;
          if (op === "between") return !!f.value.value || !!(f.value.value2 ?? null);
          return !!f.value.value;
        }
        return false;
      }).length,
    [columnFilters],
  );

  const getColumnFilterValues = useCallback(
    (columnId: string): string[] => {
      const filter = columnFilters.find((f) => f.id === columnId);
      return Array.isArray(filter?.value) ? (filter.value as string[]) : [];
    },
    [columnFilters],
  );

  const getTextFilter = useCallback(
    (columnId: string): TextFilterValue | undefined => {
      const filter = columnFilters.find((f) => f.id === columnId);
      return isTextFilter(filter?.value) ? (filter.value as TextFilterValue) : undefined;
    },
    [columnFilters],
  );

  const toggleFilterValue = useCallback((columnId: string, value: string) => {
    setColumnFilters((prev) => {
      const existing = prev.find((f) => f.id === columnId);
      const currentValues: string[] = Array.isArray(existing?.value)
        ? (existing.value as string[])
        : [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      if (newValues.length === 0) {
        return prev.filter((f) => f.id !== columnId);
      }
      if (existing) {
        return prev.map((f) => (f.id === columnId ? { ...f, value: newValues } : f));
      }
      return [...prev, { id: columnId, value: newValues }];
    });
  }, []);

  const setTextFilter = useCallback((columnId: string, next: TextFilterValue | null) => {
    setColumnFilters((prev) => {
      const without = prev.filter((f) => f.id !== columnId);
      if (!next) return without;
      return [...without, { id: columnId, value: next }];
    });
  }, []);

  const getNumberFilter = useCallback(
    (columnId: string): NumberFilterValue | undefined => {
      const filter = columnFilters.find((f) => f.id === columnId);
      return isNumberFilter(filter?.value) ? (filter.value as NumberFilterValue) : undefined;
    },
    [columnFilters],
  );

  const setNumberFilter = useCallback((columnId: string, next: NumberFilterValue | null) => {
    setColumnFilters((prev) => {
      const without = prev.filter((f) => f.id !== columnId);
      if (!next) return without;
      return [...without, { id: columnId, value: next }];
    });
  }, []);

  const getDateFilter = useCallback(
    (columnId: string): DateFilterValue | undefined => {
      const filter = columnFilters.find((f) => f.id === columnId);
      return isDateFilter(filter?.value) ? (filter.value as DateFilterValue) : undefined;
    },
    [columnFilters],
  );

  const setDateFilter = useCallback((columnId: string, next: DateFilterValue | null) => {
    setColumnFilters((prev) => {
      const without = prev.filter((f) => f.id !== columnId);
      if (!next) return without;
      return [...without, { id: columnId, value: next }];
    });
  }, []);

  const clearColumnFilter = useCallback((columnId: string) => {
    setColumnFilters((prev) => prev.filter((f) => f.id !== columnId));
  }, []);

  const clearAllFilters = useCallback(() => {
    setColumnFilters([]);
  }, []);

  // Helper to render editable cell
  const renderEditableCell = useCallback(
    (
      employee: EmployeeWithManagerName,
      field: string,
      displayValue: React.ReactNode,
      columnId: string,
      editConfig?: { type?: string; placeholder?: string },
    ) => {
      const isEditing = editingCell?.rowId === employee.id && editingCell?.columnId === columnId;

      // Select-type fields
      const selectConfig = selectColumns[field];
      if (selectConfig && isEditing) {
        return (
          <SearchableSelect
            value={(employee as any)[field] || ""}
            onValueChange={(value) => {
              handleSaveField(employee, field, value);
              setEditingCell(null);
            }}
            options={[...selectConfig.options].map((o) => ({ value: o.value, label: o.label }))}
            placeholder={selectConfig.placeholder}
            triggerClassName="!h-auto !min-h-0 !border-0 !shadow-none !bg-transparent !px-0 !py-0 !rounded-none focus:!ring-0 !text-[13px] !font-normal !text-[hsl(var(--ink-primary))]"
            popoverClassName="min-w-[220px]"
            data-testid={`${selectConfig.testIdPrefix}-${employee.id}`}
          />
        );
      }

      // Salary table field (dynamic options from API)
      if (field === "salaryTableId" && isEditing) {
        return (
          <SearchableSelect
            value={employee.salaryTableId || ""}
            onValueChange={(value) => {
              handleSaveField(employee, "salaryTableId", value || null);
              setEditingCell(null);
            }}
            options={salaryTables.map((st) => ({ value: st.id, label: st.name }))}
            placeholder="Selecione a tabela salarial"
            triggerClassName="!h-auto !min-h-0 !border-0 !shadow-none !bg-transparent !px-0 !py-0 !rounded-none focus:!ring-0 !text-[13px] !font-normal !text-[hsl(var(--ink-primary))]"
            popoverClassName="min-w-[220px]"
            data-testid={`select-salary-table-${employee.id}`}
          />
        );
      }

      // Manager field (special case)
      if (field === "directManagerId" && isEditing) {
        return (
          <SearchableSelect
            value={employee.directManagerId || ""}
            onValueChange={(value) => {
              handleSaveField(employee, "directManagerId", value || null);
              setEditingCell(null);
            }}
            options={employees
              .filter((m) => m.id !== employee.id)
              .map((m) => ({ value: m.id, label: m.fullName }))}
            placeholder="Selecione o gestor"
            triggerClassName="!h-auto !min-h-0 !border-0 !shadow-none !bg-transparent !px-0 !py-0 !rounded-none focus:!ring-0 !text-[13px] !font-normal !text-[hsl(var(--ink-primary))]"
            popoverClassName="min-w-[220px]"
            data-testid={`select-manager-${employee.id}`}
          />
        );
      }

      // Text/date/number fields
      if (isEditing) {
        return (
          <InlineEditCell
            value={String((employee as any)[field] ?? "")}
            onSave={(val) => {
              handleSaveField(employee, field, val);
              setEditingCell(null);
            }}
            onCancel={() => setEditingCell(null)}
            type={editConfig?.type}
            placeholder={editConfig?.placeholder}
          />
        );
      }

      return displayValue;
    },
    [editingCell, selectColumns, employees, salaryTables, handleSaveField],
  );

  // Define columns
  const columns = useMemo<ColumnDef<EmployeeWithManagerName>[]>(
    () =>
      keepOperatorColumns<ColumnDef<EmployeeWithManagerName>>(isEmployeeOperator, columnIdOf, [
        {
          accessorKey: "fullName",
          header: "Nome",
          size: 200,
          enableHiding: false,
          cell: ({ row }) => {
            const employee = row.original;
            const isEditing =
              editingCell?.rowId === employee.id && editingCell?.columnId === "fullName";
            if (isEditing) {
              return (
                <InlineEditCell
                  value={employee.fullName ?? ""}
                  onSave={(val) => {
                    handleSaveField(employee, "fullName", val);
                    setEditingCell(null);
                  }}
                  onCancel={() => setEditingCell(null)}
                />
              );
            }
            return (
              <button
                type="button"
                className="text-left truncate body-strong text-[hsl(var(--ink-primary))] group-hover/row:text-[hsl(var(--comp-coral))] transition-colors"
                title="Abrir detalhes"
                onClick={(e) => {
                  e.stopPropagation();
                  openSplitView({
                    title: employee.fullName,
                    openAsPageUrl: `/employees/${employee.id}`,
                    body: <EmployeeDetails embedded employeeId={employee.id} />,
                  });
                }}
              >
                {employee.fullName}
              </button>
            );
          },
        },
        {
          accessorKey: "jobTitle",
          header: "Cargo",
          size: 180,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "jobTitle",
              <span className="truncate">
                {row.original.jobTitle || <span className="text-muted-foreground">-</span>}
              </span>,
              "jobTitle",
            ),
        },
        {
          // Derived from the cargo (shared/constants/org-teams.ts) — read-only;
          // change the cargo to change the team.
          id: "team",
          accessorFn: (row) => teamForJobTitle(row.jobTitle) ?? "",
          header: "Time",
          size: 160,
          filterFn: dispatchColumnFilter,
          cell: ({ row }) => {
            const label = getOrgTeamLabel(teamForJobTitle(row.original.jobTitle));
            return (
              <span className="truncate">
                {label || <span className="text-muted-foreground">-</span>}
              </span>
            );
          },
        },
        {
          accessorKey: "compDiscipline",
          header: "Disciplina Comp",
          size: 220,
          filterFn: dispatchColumnFilter,
          cell: ({ row }) => {
            const disc = row.original.compDiscipline;
            const label = COMP_DISCIPLINES.find((d) => d.value === disc)?.label || disc;
            return renderEditableCell(
              row.original,
              "compDiscipline",
              <span className="truncate">
                {label || <span className="text-muted-foreground">-</span>}
              </span>,
              "compDiscipline",
            );
          },
        },
        {
          accessorKey: "jobLevel",
          header: "Nivel",
          size: 120,
          filterFn: dispatchColumnFilter,
          cell: ({ row }) => {
            const level = row.original.jobLevel;
            const label = JOB_LEVELS.find((l) => l.value === level)?.label || level;
            return renderEditableCell(
              row.original,
              "jobLevel",
              <span>{label || <span className="text-muted-foreground">-</span>}</span>,
              "jobLevel",
            );
          },
        },
        {
          accessorKey: "department",
          header: "Departamento",
          size: 150,
          filterFn: dispatchColumnFilter,
          cell: ({ row }) => {
            const dept = row.original.department;
            const label = DEPARTMENTS.find((d) => d.value === dept)?.label || dept;
            return renderEditableCell(
              row.original,
              "department",
              <span>{label || <span className="text-muted-foreground">-</span>}</span>,
              "department",
            );
          },
        },
        {
          accessorKey: "directManagerId",
          header: "Gestor",
          size: 200,
          enableSorting: false,
          cell: ({ row }) => {
            const name = row.original.directManagerName;
            // Same rule as the server: either employee-base key may reassign.
            if (!canMaintainEmployeeBase) {
              return name ? <span>{name}</span> : <span className="text-muted-foreground">-</span>;
            }
            return renderEditableCell(
              row.original,
              "directManagerId",
              name ? <span>{name}</span> : <span className="text-muted-foreground">-</span>,
              "directManagerId",
            );
          },
        },
        {
          accessorKey: "status",
          header: "Status",
          size: 140,
          filterFn: dispatchColumnFilter,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "status",
              <EmployeeStatus value={row.original.status} />,
              "status",
            ),
        },
        {
          accessorKey: "currentSalary",
          header: "Salário",
          size: 150,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "currentSalary",
              <span
                className={`num-mono text-right block w-full ${salaryVisible ? "" : "blur-sm select-none"}`}
              >
                {formatCurrency(row.original.currentSalary, row.original.currency)}
              </span>,
              "currentSalary",
              { type: "number" },
            ),
        },
        {
          accessorKey: "currency",
          header: "Moeda",
          size: 130,
          filterFn: dispatchColumnFilter,
          cell: ({ row }) => {
            const currency = row.original.currency;
            const label = getCurrencyLabel(currency);
            return renderEditableCell(
              row.original,
              "currency",
              <span>{label || <span className="text-muted-foreground">-</span>}</span>,
              "currency",
            );
          },
        },
        // Both columns read from the salary-tables product — hidden (and their
        // queries never fired) for whoever lacks access to it.
        ...(canSeeSalaryTables
          ? ([
              {
                id: "compaRatio",
                header: "Compa Ratio",
                size: 130,
                enableSorting: false,
                cell: ({ row }) => {
                  const emp = row.original;
                  const salary = emp.currentSalary ? Number(emp.currentSalary) : null;
                  const midValue =
                    emp.salaryTableId && emp.jobLevel
                      ? bandLookup.get(`${emp.salaryTableId}::${emp.jobLevel}`)
                      : undefined;
                  const midValueReais = midValue != null ? midValue / 100 : null;
                  const ratio = salary && midValueReais ? salary / midValueReais : null;
                  return (
                    <span
                      className={`num-mono text-right block w-full ${salaryVisible ? "" : "blur-sm select-none"}`}
                    >
                      {ratio != null ? (
                        `${(ratio * 100).toFixed(0)}%`
                      ) : (
                        <span className="text-[hsl(var(--ink-muted))]">—</span>
                      )}
                    </span>
                  );
                },
              },
              {
                accessorKey: "salaryTableId",
                header: "Tabela Salarial",
                size: 200,
                cell: ({ row }) => {
                  const tableId = row.original.salaryTableId;
                  const tableName = salaryTables.find((st) => st.id === tableId)?.name;
                  return renderEditableCell(
                    row.original,
                    "salaryTableId",
                    <span>{tableName || <span className="text-muted-foreground">-</span>}</span>,
                    "salaryTableId",
                  );
                },
              },
            ] as ColumnDef<EmployeeWithManagerName>[])
          : []),
        {
          accessorKey: "employmentType",
          header: "Tipo Contrato",
          size: 150,
          filterFn: dispatchColumnFilter,
          cell: ({ row }) => {
            const type = row.original.employmentType;
            const label = EMPLOYMENT_TYPES.find((t) => t.value === type)?.label || type;
            return renderEditableCell(
              row.original,
              "employmentType",
              <span>{label || <span className="text-muted-foreground">-</span>}</span>,
              "employmentType",
            );
          },
        },
        {
          accessorKey: "startDate",
          header: "Data Início",
          size: 140,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "startDate",
              <span className="num-mono">{formatDate(row.original.startDate)}</span>,
              "startDate",
              { type: "date" },
            ),
        },
        {
          accessorKey: "endDate",
          header: "Data Fim",
          size: 140,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "endDate",
              <span className="num-mono">{formatDate(row.original.endDate)}</span>,
              "endDate",
              { type: "date" },
            ),
        },
        {
          accessorKey: "workType",
          header: "Modelo Trabalho",
          size: 140,
          filterFn: dispatchColumnFilter,
          cell: ({ row }) => {
            const type = row.original.workType;
            const label = WORK_TYPES.find((w) => w.value === type)?.label || type;
            return renderEditableCell(
              row.original,
              "workType",
              <span>{label || <span className="text-muted-foreground">-</span>}</span>,
              "workType",
            );
          },
        },
        {
          accessorKey: "corporateEmail",
          header: "Email Corporativo",
          size: 220,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "corporateEmail",
              <span className="truncate">
                {row.original.corporateEmail || <span className="text-muted-foreground">-</span>}
              </span>,
              "corporateEmail",
              { type: "email" },
            ),
        },
        {
          accessorKey: "personalEmail",
          header: "Email Pessoal",
          size: 220,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "personalEmail",
              <span className="truncate">
                {row.original.personalEmail || <span className="text-muted-foreground">-</span>}
              </span>,
              "personalEmail",
              { type: "email" },
            ),
        },
        {
          accessorKey: "phone",
          header: "Telefone",
          size: 150,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "phone",
              <span className="num-mono">
                {row.original.phone || <span className="text-[hsl(var(--ink-muted))]">—</span>}
              </span>,
              "phone",
            ),
        },
        {
          accessorKey: "cpf",
          header: "CPF",
          size: 150,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "cpf",
              <span className="num-mono">
                {row.original.cpf || <span className="text-[hsl(var(--ink-muted))]">—</span>}
              </span>,
              "cpf",
              { placeholder: "000.000.000-00" },
            ),
        },
        {
          accessorKey: "birthdate",
          header: "Data Nascimento",
          size: 150,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "birthdate",
              <span className="num-mono">{formatDate(row.original.birthdate)}</span>,
              "birthdate",
              { type: "date" },
            ),
        },
        {
          accessorKey: "address",
          header: "Endereco",
          size: 200,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "address",
              <span className="truncate">
                {row.original.address || <span className="text-muted-foreground">-</span>}
              </span>,
              "address",
            ),
        },
        {
          accessorKey: "sop",
          header: "SOP",
          size: 150,
          cell: ({ row }) =>
            renderEditableCell(
              row.original,
              "sop",
              <span className={`num-mono ${sopVisible ? "" : "blur-sm select-none"}`}>
                {row.original.sop || <span className="text-[hsl(var(--ink-muted))]">—</span>}
              </span>,
              "sop",
            ),
        },
        {
          accessorKey: "emergencyContact",
          header: "Contato Emergencia",
          size: 180,
          enableSorting: false,
          cell: ({ row }) => {
            const value = row.original.emergencyContact as any;
            const hasContacts = value?.contacts && value.contacts.length > 0;

            return (
              <button
                type="button"
                className="flex items-center gap-2 w-full text-left cursor-pointer"
                onClick={() => {
                  setSelectedEmployeeForContact(row.original);
                  setEmergencyContactOpen(true);
                }}
              >
                {hasContacts ? (
                  <span className="text-sm">
                    {value.contacts.find((c: any) => c.isPrimary)?.name || value.contacts[0].name}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-sm">-</span>
                )}
              </button>
            );
          },
        },
        {
          accessorKey: "slackId",
          header: "Slack ID",
          size: 150,
          enableSorting: false,
          cell: ({ row }) => {
            const slackId = row.original.slackId;
            if (!slackId) return <span className="text-[hsl(var(--ink-muted))]">—</span>;
            return <span className="num-mono">{slackId.substring(0, 8)}…</span>;
          },
        },
        // Row actions column (requires employee-management permission)
        ...(canManageEmployees
          ? [
              {
                id: "actions",
                header: "",
                size: 100,
                enableSorting: false,
                enableHiding: false,
                cell: ({ row }: { row: { original: EmployeeWithManagerName } }) => (
                  <button
                    type="button"
                    className="text-[hsl(var(--alert-red))] hover:text-[hsl(var(--comp-coral-deep))] text-sm body-strong"
                    data-testid={`button-delete-employee-${row.original.id}`}
                    onClick={() => {
                      setEmployeeToDelete(row.original);
                      setDeleteConfirmOpen(true);
                    }}
                  >
                    Remover
                  </button>
                ),
              } as ColumnDef<EmployeeWithManagerName>,
            ]
          : []),
      ]),
    [
      handleSaveField,
      canMaintainEmployeeBase,
      isEmployeeOperator,
      canManageEmployees,
      editingCell,
      openSplitView,
      renderEditableCell,
      salaryVisible,
      sopVisible,
      salaryTables,
      bandLookup,
      canSeeSalaryTables,
    ],
  );

  // Non-editable columns (click should not trigger editing)
  const nonEditableColumns = useMemo(
    () =>
      new Set([
        "select",
        "team",
        "emergencyContact",
        "slackId",
        "currentSalary",
        "compaRatio",
        "actions",
      ]),
    [],
  );

  // Columns only the employee-base keys can edit (not a plain direct manager)
  const adminOnlyEditColumns = useMemo(() => new Set(["directManagerId"]), []);

  // Single editability rule for click and F2. Mirrors the server: the manager
  // column needs an employee-base key; the operator only edits its fields.
  const isColumnEditable = useCallback(
    (columnId: string) =>
      !nonEditableColumns.has(columnId) &&
      (!adminOnlyEditColumns.has(columnId) || canMaintainEmployeeBase) &&
      (!isEmployeeOperator || isEmployeeOperatorEditableField(columnId)),
    [nonEditableColumns, adminOnlyEditColumns, canMaintainEmployeeBase, isEmployeeOperator],
  );

  const table = useReactTable({
    data: employees,
    columns,
    defaultColumn: { filterFn: dispatchColumnFilter },
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnSizing,
    },
    getRowId: (row) => row.id,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // §10.8 Canonical table — keyboard shortcuts (⌘F, F2, Esc)
  useEffect(() => {
    function isTypingInField(target: EventTarget | null): boolean {
      const el = target as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true
      );
    }

    let altSeqTimer: ReturnType<typeof setTimeout> | null = null;
    const resetAltSeq = () => {
      setAltSeq([]);
      if (altSeqTimer) {
        clearTimeout(altSeqTimer);
        altSeqTimer = null;
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;

      // ⌘Z / Ctrl+Z — desfaz última edição inline
      if (mod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "z") {
        if (isTypingInField(e.target)) return; // não interfere com undo nativo no input
        e.preventDefault();
        performUndo();
        return;
      }
      // ⌘⇧Z (placeholder — redo não implementado ainda)
      if (mod && e.shiftKey && e.key.toLowerCase() === "z") {
        if (isTypingInField(e.target)) return;
        e.preventDefault();
        toast({ description: "Refazer ainda não disponível." });
        return;
      }

      // Alt+H+O+{I|R|H} — Excel-style density
      if (e.altKey && e.key.toLowerCase() === "h" && altSeq.length === 0) {
        e.preventDefault();
        setAltSeq(["alt", "h"]);
        if (altSeqTimer) clearTimeout(altSeqTimer);
        altSeqTimer = setTimeout(resetAltSeq, 3000);
        return;
      }
      if (altSeq.length === 2 && altSeq[1] === "h" && e.key.toLowerCase() === "o") {
        e.preventDefault();
        setAltSeq((prev) => [...prev, "o"]);
        if (altSeqTimer) clearTimeout(altSeqTimer);
        altSeqTimer = setTimeout(resetAltSeq, 3000);
        return;
      }
      if (altSeq.length === 3 && altSeq[2] === "o") {
        const k = e.key.toLowerCase();
        if (k === "i" || k === "r" || k === "h") {
          e.preventDefault();
          const next: Density = k === "i" ? "compact" : k === "h" ? "relaxed" : "comfortable";
          setDensity(next);
          toast({
            description: `Densidade: ${next === "compact" ? "Compacta" : next === "relaxed" ? "Alta" : "Confortável"} (Alt+H+O+${k.toUpperCase()})`,
          });
          resetAltSeq();
          return;
        }
      }

      // ⌘F / Ctrl+F — abre search inline da tabela
      if (mod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "f") {
        const wrap = tableWrapRef.current;
        if (!wrap) return;
        const rect = wrap.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (!inView) return;
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 30);
        return;
      }

      // F2 — edita a última célula clicada (se for editável)
      if (e.key === "F2" && !isTypingInField(e.target)) {
        const last = lastClickedCellRef.current;
        if (!last) return;
        if (!isColumnEditable(last.columnId) || last.columnId === "currentSalary") return;
        e.preventDefault();
        setEditingCell({ rowId: last.rowId, columnId: last.columnId });
        return;
      }

      // Esc — cancela em cascata: edit → search → seleção em lote
      if (e.key === "Escape") {
        if (editingCell) {
          // InlineEditCell handles its own Esc; deixa propagar.
          return;
        }
        if (searchOpen || (globalFilter?.length ?? 0) > 0) {
          e.preventDefault();
          setGlobalFilter("");
          setSearchOpen(false);
          searchInputRef.current?.blur();
          return;
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (altSeqTimer) clearTimeout(altSeqTimer);
    };
  }, [editingCell, searchOpen, globalFilter, isColumnEditable, altSeq, performUndo, toast]);

  const handleDownloadXlsx = useCallback(() => {
    const rows = table.getFilteredRowModel().rows.map((r) => r.original);

    const data = rows.map((emp) => ({
      Nome: emp.fullName || "",
      Cargo: emp.jobTitle || "",
      Time: getOrgTeamLabel(teamForJobTitle(emp.jobTitle)) || "",
      "Disciplina Comp":
        COMP_DISCIPLINES.find((d) => d.value === emp.compDiscipline)?.label ||
        emp.compDiscipline ||
        "",
      Nivel: JOB_LEVELS.find((l) => l.value === emp.jobLevel)?.label || emp.jobLevel || "",
      Departamento:
        DEPARTMENTS.find((d) => d.value === emp.department)?.label || emp.department || "",
      Gestor: emp.directManagerName || "",
      Status: EMPLOYEE_STATUSES.find((s) => s.value === emp.status)?.label || emp.status || "",
      Salario: emp.currentSalary ? Number(emp.currentSalary) : "",
      Moeda: getCurrencyLabel(emp.currency),
      // Salary-tables-derived fields follow the same gate as their columns.
      ...(canSeeSalaryTables
        ? {
            "Compa Ratio": (() => {
              const salary = emp.currentSalary ? Number(emp.currentSalary) : null;
              const midValue =
                emp.salaryTableId && emp.jobLevel
                  ? bandLookup.get(`${emp.salaryTableId}::${emp.jobLevel}`)
                  : undefined;
              const midValueReais = midValue != null ? midValue / 100 : null;
              return salary && midValueReais
                ? `${((salary / midValueReais) * 100).toFixed(0)}%`
                : "";
            })(),
            "Tabela Salarial": salaryTables.find((st) => st.id === emp.salaryTableId)?.name || "",
          }
        : {}),
      "Tipo Contrato":
        EMPLOYMENT_TYPES.find((t) => t.value === emp.employmentType)?.label ||
        emp.employmentType ||
        "",
      "Data Inicio": emp.startDate ? formatDate(emp.startDate) : "",
      "Data Fim": emp.endDate ? formatDate(emp.endDate) : "",
      "Modelo Trabalho":
        WORK_TYPES.find((w) => w.value === emp.workType)?.label || emp.workType || "",
      "Email Corporativo": emp.corporateEmail || "",
      "Email Pessoal": emp.personalEmail || "",
      Telefone: emp.phone || "",
      CPF: emp.cpf || "",
      "Data Nascimento": emp.birthdate ? formatDate(emp.birthdate) : "",
      SOP: emp.sop || "",
      Endereco: emp.address || "",
    }));

    // The operator's rows carry no compensation or personal data — export only
    // the columns it actually has instead of a sheet of blank sensitive ones.
    const exported = isEmployeeOperator
      ? data.map((row) =>
          Object.fromEntries(
            OPERATOR_EXPORT_COLUMNS.map((col) => [col, row[col as keyof typeof row] ?? ""]),
          ),
        )
      : data;

    const ws = XLSX.utils.json_to_sheet(exported);

    const colWidths = [
      { wch: 30 },
      { wch: 25 },
      { wch: 18 },
      { wch: 30 },
      { wch: 18 },
      { wch: 18 },
      { wch: 25 },
      { wch: 12 },
      { wch: 15 },
      { wch: 14 },
      { wch: 25 },
      { wch: 15 },
      { wch: 14 },
      { wch: 14 },
      { wch: 15 },
      { wch: 30 },
      { wch: 30 },
      { wch: 18 },
      { wch: 16 },
      { wch: 14 },
      { wch: 15 },
      { wch: 30 },
    ];
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Colaboradores");
    XLSX.writeFile(wb, `colaboradores_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [table, salaryTables, bandLookup, canSeeSalaryTables, isEmployeeOperator]);

  // Mobile block
  if (isMobile) {
    return (
      <div className="space-y-6">
        <PageHeader title="Colaboradores" description="Gerencie sua equipe" />
        <div className="bg-muted rounded-lg p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Apenas Desktop</h2>
          <p className="text-muted-foreground">
            A tabela de colaboradores foi otimizada para uso em desktop. Acesse esta pagina a partir
            de um computador para gerenciar colaboradores.
          </p>
        </div>
      </div>
    );
  }

  const totalRows = table.getFilteredRowModel().rows.length;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      <PageHeader title="Colaboradores" description="Gerencie sua equipe">
        {canManageEmployees && (
          <Button
            data-testid="button-add-employee"
            onClick={() => setAddEmployeeOpen(true)}
            size="sm"
          >
            Cadastrar colaborador
          </Button>
        )}
      </PageHeader>

      {/* Alt-sequence indicator (Excel-style density) */}
      {altSeq.length > 0 && (
        <div className="fixed top-3 right-3 z-50 flex items-center gap-1.5 px-3 py-1.5 bg-[hsl(var(--ink-primary))] text-[hsl(var(--ink-on-color))] rounded-full text-[12px] font-medium shadow-lg">
          <span className={altSeq.includes("alt") ? "" : "opacity-40"}>Alt</span>
          <span className="opacity-40">+</span>
          <span className={altSeq.includes("h") ? "" : "opacity-40"}>H</span>
          <span className="opacity-40">+</span>
          <span className={altSeq.includes("o") ? "" : "opacity-40"}>O</span>
          <span className="opacity-40">+</span>
          <span className="opacity-60">I/R/H</span>
        </div>
      )}

      {/* Canonical table (§10.8): wrap → toolbar → filter pills → table.
          flex-1 + min-h-0 garante que o wrap ocupa altura restante de <main>
          sem deixar <main> scrollar — o scroll vertical fica DENTRO da tabela. */}
      <div className="tbl-wrap flex flex-col flex-1 min-h-0" ref={tableWrapRef}>
        {/* Toolbar */}
        <div className="tbl-toolbar">
          <div className="tbl-toolbar-left">
            <span className="tbl-toolbar-title">Headcount</span>
            <span className="tbl-toolbar-count">
              {totalRows} {totalRows === 1 ? "colaborador" : "colaboradores"}
            </span>
          </div>
          <div className="tbl-toolbar-right">
            <input
              ref={searchInputRef}
              id="employees-search"
              className={`tbl-search-inline ${searchOpen || (globalFilter?.length ?? 0) > 0 ? "is-open" : ""}`}
              placeholder="Buscar nesta tabela…"
              value={globalFilter ?? ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              onBlur={() => {
                if (!(globalFilter?.length ?? 0)) setSearchOpen(false);
              }}
              data-testid="input-employee-search"
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="tbl-action tbl-action-icon"
                  aria-label="Buscar"
                  onClick={() => {
                    setSearchOpen((v) => !v);
                    setTimeout(() => searchInputRef.current?.focus(), 50);
                  }}
                  data-testid="button-toolbar-search"
                >
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-3.5-3.5" />
                  </svg>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Buscar · ⌘F</TooltipContent>
            </Tooltip>
            {!isEmployeeOperator && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="tbl-action tbl-action-icon"
                    data-active={valuesVisible ? "true" : undefined}
                    aria-label={
                      valuesVisible ? "Ocultar valores sensíveis" : "Mostrar valores sensíveis"
                    }
                    onClick={() => setValuesVisible((v) => !v)}
                    data-testid="button-toggle-sensitive-values"
                  >
                    {valuesVisible ? (
                      <svg
                        aria-hidden="true"
                        focusable="false"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                        <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                        <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                        <line x1="2" y1="2" x2="22" y2="22" />
                      </svg>
                    ) : (
                      <svg
                        aria-hidden="true"
                        focusable="false"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {valuesVisible
                    ? "Ocultar salário, compa ratio e SOP"
                    : "Mostrar salário, compa ratio e SOP"}
                </TooltipContent>
              </Tooltip>
            )}
            <Popover>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="tbl-action tbl-action-icon relative"
                      data-active={activeFilterCount > 0 ? "true" : undefined}
                      aria-label={
                        activeFilterCount > 0 ? `Filtros · ${activeFilterCount} ativos` : "Filtrar"
                      }
                      data-testid="button-filters-menu"
                    >
                      <svg
                        aria-hidden="true"
                        focusable="false"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                      </svg>
                      {activeFilterCount > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-[hsl(var(--comp-coral))] text-[9.5px] font-semibold leading-[14px] text-white tabular-nums text-center pointer-events-none">
                          {activeFilterCount}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {activeFilterCount > 0 ? `Filtros · ${activeFilterCount} ativos` : "Filtrar"}
                </TooltipContent>
              </Tooltip>
              <PopoverContent align="end" className="w-[320px] p-0 max-h-[70vh] overflow-auto">
                <div className="p-3 border-b border-[hsl(var(--sand-divider))]">
                  <div className="flex items-center justify-between">
                    <span className="body-strong">Filtros</span>
                    {activeFilterCount > 0 && (
                      <button
                        type="button"
                        className="text-xs text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))] underline"
                        onClick={clearAllFilters}
                      >
                        limpar todos
                      </button>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-[hsl(var(--sand-divider))]">
                  {filterableColumns.map((fc) => {
                    if (fc.kind === "enum") {
                      const selectedValues = getColumnFilterValues(fc.columnId);
                      const isActive = selectedValues.length > 0;
                      return (
                        <details key={fc.columnId} open={isActive} className="group">
                          <summary className="flex items-center justify-between px-3 py-2 cursor-pointer text-sm">
                            <span className={isActive ? "body-strong" : ""}>{fc.label}</span>
                            {isActive && (
                              <span className="caption-mono text-[hsl(var(--ink-muted))]">
                                {selectedValues.length}
                              </span>
                            )}
                          </summary>
                          <div className="px-3 pb-2 space-y-1 max-h-[200px] overflow-auto">
                            {fc.options.map((opt) => (
                              <label
                                key={opt.value}
                                className="flex items-center gap-2 py-0.5 text-sm cursor-pointer hover:text-[hsl(var(--ink-primary))] text-[hsl(var(--ink-muted))]"
                              >
                                <input
                                  type="checkbox"
                                  className="h-3.5 w-3.5 accent-[hsl(var(--ink-primary))]"
                                  checked={selectedValues.includes(opt.value)}
                                  onChange={() => toggleFilterValue(fc.columnId, opt.value)}
                                />
                                <span className="truncate">{opt.label}</span>
                              </label>
                            ))}
                            {isActive && (
                              <button
                                type="button"
                                className="text-xs text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))] underline pt-1"
                                onClick={() => clearColumnFilter(fc.columnId)}
                              >
                                limpar
                              </button>
                            )}
                          </div>
                        </details>
                      );
                    }
                    if (fc.kind === "text") {
                      const tf = getTextFilter(fc.columnId);
                      const opMeta = TEXT_OPS.find((o) => o.value === (tf?.op ?? "contains"));
                      const isActive =
                        !!tf && (!opMeta?.needsValue || (tf.value ?? "").trim().length > 0);
                      return (
                        <details key={fc.columnId} open={isActive} className="group">
                          <summary className="flex items-center justify-between px-3 py-2 cursor-pointer text-sm">
                            <span className={isActive ? "body-strong" : ""}>{fc.label}</span>
                            {isActive && (
                              <span className="caption-mono text-[hsl(var(--ink-muted))]">
                                {opMeta?.short}
                              </span>
                            )}
                          </summary>
                          <div className="px-3 pb-2 space-y-2">
                            <Select
                              value={tf?.op ?? "contains"}
                              onValueChange={(v) =>
                                setTextFilter(fc.columnId, {
                                  kind: "text",
                                  op: v as TextOp,
                                  value: tf?.value ?? "",
                                })
                              }
                            >
                              <SelectTrigger className="h-8 text-[13px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TEXT_OPS.map((op) => (
                                  <SelectItem key={op.value} value={op.value}>
                                    {op.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {opMeta?.needsValue ? (
                              <input
                                type="text"
                                value={tf?.value ?? ""}
                                placeholder="Digite um valor…"
                                onChange={(e) =>
                                  setTextFilter(fc.columnId, {
                                    kind: "text",
                                    op: tf?.op ?? "contains",
                                    value: e.target.value,
                                  })
                                }
                                className="w-full h-8 px-2 text-[13px] rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-white))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--comp-coral))]/30"
                              />
                            ) : null}
                            {isActive && (
                              <button
                                type="button"
                                className="text-xs text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))] underline"
                                onClick={() => clearColumnFilter(fc.columnId)}
                              >
                                limpar
                              </button>
                            )}
                          </div>
                        </details>
                      );
                    }
                    if (fc.kind === "number") {
                      const nf = getNumberFilter(fc.columnId);
                      const opMeta = NUMBER_OPS.find((o) => o.value === (nf?.op ?? "eq"));
                      const isActive =
                        !!nf &&
                        (opMeta?.needsValue === 0 ||
                          (opMeta?.needsValue === 2
                            ? nf.value !== null || (nf.value2 ?? null) !== null
                            : nf.value !== null));
                      const numClass =
                        "w-full h-8 px-2 text-[13px] rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-white))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--comp-coral))]/30 tabular-nums";
                      return (
                        <details key={fc.columnId} open={isActive} className="group">
                          <summary className="flex items-center justify-between px-3 py-2 cursor-pointer text-sm">
                            <span className={isActive ? "body-strong" : ""}>{fc.label}</span>
                            {isActive && (
                              <span className="caption-mono text-[hsl(var(--ink-muted))]">
                                {opMeta?.short}
                              </span>
                            )}
                          </summary>
                          <div className="px-3 pb-2 space-y-2">
                            <Select
                              value={nf?.op ?? "eq"}
                              onValueChange={(v) =>
                                setNumberFilter(fc.columnId, {
                                  kind: "number",
                                  op: v as NumberOp,
                                  value: nf?.value ?? null,
                                  value2: nf?.value2 ?? null,
                                })
                              }
                            >
                              <SelectTrigger className="h-8 text-[13px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {NUMBER_OPS.map((op) => (
                                  <SelectItem key={op.value} value={op.value}>
                                    {op.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {(opMeta?.needsValue ?? 0) >= 1 && (
                              <input
                                type="number"
                                inputMode="decimal"
                                value={nf?.value ?? ""}
                                placeholder={opMeta?.needsValue === 2 ? "De…" : "Valor"}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const parsed = raw === "" ? null : Number(raw);
                                  setNumberFilter(fc.columnId, {
                                    kind: "number",
                                    op: nf?.op ?? "eq",
                                    value: Number.isFinite(parsed as number)
                                      ? (parsed as number)
                                      : null,
                                    value2: nf?.value2 ?? null,
                                  });
                                }}
                                className={numClass}
                              />
                            )}
                            {opMeta?.needsValue === 2 && (
                              <input
                                type="number"
                                inputMode="decimal"
                                value={nf?.value2 ?? ""}
                                placeholder="Até…"
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const parsed = raw === "" ? null : Number(raw);
                                  setNumberFilter(fc.columnId, {
                                    kind: "number",
                                    op: nf?.op ?? "eq",
                                    value: nf?.value ?? null,
                                    value2: Number.isFinite(parsed as number)
                                      ? (parsed as number)
                                      : null,
                                  });
                                }}
                                className={numClass}
                              />
                            )}
                            {isActive && (
                              <button
                                type="button"
                                className="text-xs text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))] underline"
                                onClick={() => clearColumnFilter(fc.columnId)}
                              >
                                limpar
                              </button>
                            )}
                          </div>
                        </details>
                      );
                    }
                    // date
                    const df = getDateFilter(fc.columnId);
                    const opMeta = DATE_OPS.find((o) => o.value === (df?.op ?? "on"));
                    const isActive =
                      !!df &&
                      (opMeta?.needsValue === 0 ||
                        (opMeta?.needsValue === 2
                          ? !!df.value || !!(df.value2 ?? null)
                          : !!df.value));
                    const dateClass =
                      "w-full h-8 px-2 text-[13px] rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-white))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--comp-coral))]/30 tabular-nums";
                    return (
                      <details key={fc.columnId} open={isActive} className="group">
                        <summary className="flex items-center justify-between px-3 py-2 cursor-pointer text-sm">
                          <span className={isActive ? "body-strong" : ""}>{fc.label}</span>
                          {isActive && (
                            <span className="caption-mono text-[hsl(var(--ink-muted))]">
                              {opMeta?.short}
                            </span>
                          )}
                        </summary>
                        <div className="px-3 pb-2 space-y-2">
                          <Select
                            value={df?.op ?? "on"}
                            onValueChange={(v) =>
                              setDateFilter(fc.columnId, {
                                kind: "date",
                                op: v as DateOp,
                                value: df?.value ?? null,
                                value2: df?.value2 ?? null,
                              })
                            }
                          >
                            <SelectTrigger className="h-8 text-[13px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DATE_OPS.map((op) => (
                                <SelectItem key={op.value} value={op.value}>
                                  {op.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {(opMeta?.needsValue ?? 0) >= 1 && (
                            <input
                              type="date"
                              value={df?.value ?? ""}
                              onChange={(e) =>
                                setDateFilter(fc.columnId, {
                                  kind: "date",
                                  op: df?.op ?? "on",
                                  value: e.target.value || null,
                                  value2: df?.value2 ?? null,
                                })
                              }
                              className={dateClass}
                            />
                          )}
                          {opMeta?.needsValue === 2 && (
                            <input
                              type="date"
                              value={df?.value2 ?? ""}
                              onChange={(e) =>
                                setDateFilter(fc.columnId, {
                                  kind: "date",
                                  op: df?.op ?? "on",
                                  value: df?.value ?? null,
                                  value2: e.target.value || null,
                                })
                              }
                              className={dateClass}
                            />
                          )}
                          {isActive && (
                            <button
                              type="button"
                              className="text-xs text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))] underline"
                              onClick={() => clearColumnFilter(fc.columnId)}
                            >
                              limpar
                            </button>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="tbl-action" data-testid="button-actions-menu">
                  Ações
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  data-testid="button-open-column-manager"
                  onClick={() => setColumnManagerOpen(true)}
                >
                  Configurar colunas
                </DropdownMenuItem>
                {canManageEmployees && (
                  <DropdownMenuItem
                    data-testid="button-upload-data"
                    onClick={() => setDataUploadOpen(true)}
                  >
                    Importar planilha
                  </DropdownMenuItem>
                )}
                {canMaintainEmployeeBase && (
                  <DropdownMenuItem onClick={() => setSlackSyncOpen(true)}>
                    Sincronizar com Slack
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  data-testid="button-download-employees"
                  onClick={handleDownloadXlsx}
                  disabled={!employees.length}
                >
                  Exportar planilha
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <ColumnManagerSheet
            open={columnManagerOpen}
            onOpenChange={setColumnManagerOpen}
            table={table}
            onColumnOrderChange={setColumnOrder}
          />
        </div>

        {/* Filter pills bar — Notion-style (§10.10) */}
        <div className={`tbl-filterbar ${activeFilterCount > 0 ? "is-open" : ""}`}>
          <span className="tbl-filterbar-label">Filtros</span>
          {columnFilters.map((f) => {
            const config = filterableColumns.find((fc) => fc.columnId === f.id);
            if (!config) return null;
            let body: React.ReactNode = null;
            if (config.kind === "enum") {
              if (!Array.isArray(f.value) || (f.value as string[]).length === 0) return null;
              const labels = (f.value as string[])
                .map((v) => config.options.find((o) => o.value === v)?.label || v)
                .join(" ou ");
              body = (
                <>
                  <strong>{config.label}:</strong> {labels}
                </>
              );
            } else if (config.kind === "text" && isTextFilter(f.value)) {
              const tf = f.value as TextFilterValue;
              const opMeta = TEXT_OPS.find((o) => o.value === tf.op);
              if (!opMeta) return null;
              const active = !opMeta.needsValue || (tf.value ?? "").trim().length > 0;
              if (!active) return null;
              body = (
                <>
                  <strong>{config.label}</strong> {opMeta.short}
                  {opMeta.needsValue ? <> "{tf.value}"</> : null}
                </>
              );
            } else if (config.kind === "number" && isNumberFilter(f.value)) {
              const nf = f.value as NumberFilterValue;
              const opMeta = NUMBER_OPS.find((o) => o.value === nf.op);
              if (!opMeta) return null;
              const fmt = (n: number | null | undefined) =>
                n === null || n === undefined ? "—" : n.toLocaleString("pt-BR");
              const active =
                opMeta.needsValue === 0 ||
                (opMeta.needsValue === 2
                  ? nf.value !== null || (nf.value2 ?? null) !== null
                  : nf.value !== null);
              if (!active) return null;
              body = (
                <>
                  <strong>{config.label}</strong> {opMeta.short}
                  {opMeta.needsValue === 1 && <> {fmt(nf.value)}</>}
                  {opMeta.needsValue === 2 && (
                    <>
                      {" "}
                      {fmt(nf.value)} e {fmt(nf.value2)}
                    </>
                  )}
                </>
              );
            } else if (config.kind === "date" && isDateFilter(f.value)) {
              const df = f.value as DateFilterValue;
              const opMeta = DATE_OPS.find((o) => o.value === df.op);
              if (!opMeta) return null;
              const fmt = (s: string | null | undefined) => s ?? "—";
              const active =
                opMeta.needsValue === 0 ||
                (opMeta.needsValue === 2 ? !!df.value || !!(df.value2 ?? null) : !!df.value);
              if (!active) return null;
              body = (
                <>
                  <strong>{config.label}</strong> {opMeta.short}
                  {opMeta.needsValue === 1 && <> {fmt(df.value)}</>}
                  {opMeta.needsValue === 2 && (
                    <>
                      {" "}
                      {fmt(df.value)} e {fmt(df.value2)}
                    </>
                  )}
                </>
              );
            }
            if (!body) return null;
            return (
              <span key={f.id} className="filter-pill">
                {body}
                <button
                  type="button"
                  className="filter-pill-close"
                  aria-label={`Remover filtro ${config.label}`}
                  onClick={() => clearColumnFilter(f.id)}
                >
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <path d="m18 6-12 12M6 6l12 12" />
                  </svg>
                </button>
              </span>
            );
          })}
          {activeFilterCount > 0 && (
            <button
              type="button"
              className="tbl-action ml-auto !text-[11.5px]"
              onClick={clearAllFilters}
              data-testid="button-clear-all-filters"
            >
              Limpar tudo
            </button>
          )}
        </div>

        {/* Table — outer div é o scroll vertical (sticky thead gruda nele);
            shadcn <Table> tem wrapper interno overflow-auto pro scroll horizontal.
            Anulamos o overflow-y do interno pra sticky funcionar. */}
        {/* O wrapper do <Table> (shadcn) é o ÚNICO scrollport — recebe a maxHeight diretamente.
            sticky-top em <th> gruda nele; sticky-left na coluna Nome também (mesmo scrollport). */}
        <Table
          wrapperClassName="flex-1 min-h-0"
          wrapperStyle={{ maxHeight: "calc(100vh - 220px)" }}
          className="[&_td]:border-b [&_td]:border-[hsl(var(--sand-divider-2))] [&_th]:border-b [&_th]:border-[hsl(var(--sand-divider))]"
          style={{
            width: table.getCenterTotalSize(),
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className="border-b border-[hsl(var(--sand-divider))] hover:bg-transparent"
              >
                {headerGroup.headers.map((header) => {
                  const isPinned = header.column.id === "fullName";
                  const sortDir = header.column.getIsSorted();
                  const canSort = header.column.getCanSort();
                  const canHide = header.column.getCanHide();
                  const filterConfig = filterableColumns.find(
                    (fc) => fc.columnId === header.column.id,
                  );
                  const selectedFilterValues =
                    filterConfig?.kind === "enum"
                      ? getColumnFilterValues(filterConfig.columnId)
                      : [];
                  const headerTextFilter =
                    filterConfig?.kind === "text"
                      ? getTextFilter(filterConfig.columnId)
                      : undefined;
                  const headerTextActive =
                    !!headerTextFilter &&
                    (headerTextFilter.op === "is_empty" ||
                      headerTextFilter.op === "not_empty" ||
                      (headerTextFilter.value ?? "").trim().length > 0);
                  const headerNumberFilter =
                    filterConfig?.kind === "number"
                      ? getNumberFilter(filterConfig.columnId)
                      : undefined;
                  const headerNumberActive =
                    !!headerNumberFilter &&
                    (headerNumberFilter.op === "is_empty" ||
                      headerNumberFilter.op === "not_empty" ||
                      (headerNumberFilter.op === "between"
                        ? headerNumberFilter.value !== null ||
                          (headerNumberFilter.value2 ?? null) !== null
                        : headerNumberFilter.value !== null));
                  const headerDateFilter =
                    filterConfig?.kind === "date"
                      ? getDateFilter(filterConfig.columnId)
                      : undefined;
                  const headerDateActive =
                    !!headerDateFilter &&
                    (headerDateFilter.op === "is_empty" ||
                      headerDateFilter.op === "not_empty" ||
                      (headerDateFilter.op === "between"
                        ? !!headerDateFilter.value || !!(headerDateFilter.value2 ?? null)
                        : !!headerDateFilter.value));
                  const hasFilter =
                    selectedFilterValues.length > 0 ||
                    headerTextActive ||
                    headerNumberActive ||
                    headerDateActive;
                  const hasCustomWidth = columnSizing[header.column.id] != null;
                  const showMenu =
                    header.column.id !== "select" &&
                    header.column.id !== "actions" &&
                    (canSort || canHide || !!filterConfig);
                  // sticky-top per-th (robust against border-collapse). Pinned column gets sticky-left + higher z-index.
                  const stickyClasses = isPinned ? "sticky top-0 left-0 z-30" : "sticky top-0 z-20";
                  const headerLabel = header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext());
                  const labelNode = (
                    <span className="inline-flex items-center gap-1.5">
                      {headerLabel}
                      {sortDir && (
                        <span className="text-[hsl(var(--ink-muted))]">
                          {sortDir === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                      {hasFilter && <span className="text-[hsl(var(--comp-coral))]">•</span>}
                    </span>
                  );
                  return (
                    <TableHead
                      key={header.id}
                      data-sortable={canSort ? "true" : undefined}
                      className={`${stickyClasses} h-9 px-3.5 py-2.5 whitespace-nowrap text-[10.5px] font-semibold uppercase tracking-[0.02em] text-[hsl(var(--ink-muted))] bg-[hsl(var(--paper-white))] ${
                        showMenu ? "hover:text-[hsl(var(--ink-primary))]" : ""
                      }`}
                      style={{ width: header.getSize() }}
                    >
                      {showMenu ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1.5 text-left uppercase tracking-[0.02em] font-semibold text-[10.5px] text-inherit hover:text-[hsl(var(--ink-primary))] focus:outline-none focus-visible:text-[hsl(var(--ink-primary))]"
                              data-testid={`header-menu-${header.column.id}`}
                            >
                              {labelNode}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56">
                            {canSort && (
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger
                                  data-testid={`header-sort-${header.column.id}`}
                                >
                                  <span className="flex-1">Ordenar</span>
                                  {sortDir && (
                                    <span className="mr-1 text-[hsl(var(--ink-muted))] text-[11px]">
                                      {sortDir === "asc" ? "crescente" : "decrescente"}
                                    </span>
                                  )}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-48">
                                  <DropdownMenuItem
                                    onClick={() => header.column.toggleSorting(false)}
                                    data-testid={`header-sort-asc-${header.column.id}`}
                                  >
                                    <span className="flex-1">Ordenar crescente</span>
                                    {sortDir === "asc" && (
                                      <span className="text-[hsl(var(--comp-coral))]">✓</span>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => header.column.toggleSorting(true)}
                                    data-testid={`header-sort-desc-${header.column.id}`}
                                  >
                                    <span className="flex-1">Ordenar decrescente</span>
                                    {sortDir === "desc" && (
                                      <span className="text-[hsl(var(--comp-coral))]">✓</span>
                                    )}
                                  </DropdownMenuItem>
                                  {sortDir && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => header.column.clearSorting()}
                                        data-testid={`header-sort-clear-${header.column.id}`}
                                      >
                                        Limpar ordenação
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            )}
                            {filterConfig && (
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger
                                  data-testid={`header-filter-${header.column.id}`}
                                >
                                  <span className="flex-1">Filtrar</span>
                                  {filterConfig.kind === "enum" && hasFilter && (
                                    <span className="mr-1 text-[hsl(var(--ink-muted))] text-[11px] tabular-nums">
                                      {selectedFilterValues.length}
                                    </span>
                                  )}
                                  {((filterConfig.kind === "text" && headerTextActive) ||
                                    (filterConfig.kind === "number" && headerNumberActive) ||
                                    (filterConfig.kind === "date" && headerDateActive)) && (
                                    <span className="mr-1 text-[hsl(var(--comp-coral))] text-[11px]">
                                      •
                                    </span>
                                  )}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent
                                  className={filterConfig.kind === "enum" ? "w-56 p-1" : "w-64 p-2"}
                                >
                                  {filterConfig.kind === "enum" && (
                                    <>
                                      <div className="max-h-[260px] overflow-auto">
                                        {filterConfig.options.map((opt) => {
                                          const checked = selectedFilterValues.includes(opt.value);
                                          return (
                                            <label
                                              key={opt.value}
                                              className="flex items-center gap-2 px-1.5 py-1 text-[13px] cursor-pointer rounded-sm hover:bg-[hsl(var(--sand-step))] text-[hsl(var(--ink-primary))]"
                                            >
                                              <input
                                                type="checkbox"
                                                className="h-3.5 w-3.5 accent-[hsl(var(--ink-primary))]"
                                                checked={checked}
                                                onChange={() =>
                                                  toggleFilterValue(
                                                    filterConfig.columnId,
                                                    opt.value,
                                                  )
                                                }
                                              />
                                              <span className="truncate">{opt.label}</span>
                                            </label>
                                          );
                                        })}
                                      </div>
                                      {hasFilter && (
                                        <>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            onClick={() => clearColumnFilter(filterConfig.columnId)}
                                            data-testid={`header-filter-clear-${header.column.id}`}
                                          >
                                            Limpar filtro
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </>
                                  )}
                                  {filterConfig.kind === "text" &&
                                    (() => {
                                      const current: TextFilterValue = headerTextFilter ?? {
                                        kind: "text",
                                        op: "contains",
                                        value: "",
                                      };
                                      const currentOp =
                                        TEXT_OPS.find((o) => o.value === current.op) ?? TEXT_OPS[0];
                                      return (
                                        // biome-ignore lint/a11y/noStaticElementInteractions: wrapper stops keystrokes from bubbling to dropdown's typeahead while inputs receive them
                                        <div
                                          className="flex flex-col gap-2"
                                          onKeyDown={(e) => e.stopPropagation()}
                                        >
                                          <Select
                                            value={current.op}
                                            onValueChange={(v) =>
                                              setTextFilter(filterConfig.columnId, {
                                                kind: "text",
                                                op: v as TextOp,
                                                value: current.value,
                                              })
                                            }
                                          >
                                            <SelectTrigger
                                              className="h-8 text-[13px]"
                                              data-testid={`header-filter-op-${header.column.id}`}
                                            >
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {TEXT_OPS.map((op) => (
                                                <SelectItem key={op.value} value={op.value}>
                                                  {op.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          {currentOp.needsValue && (
                                            <input
                                              type="text"
                                              // biome-ignore lint/a11y/noAutofocus: filter input is the primary affordance once submenu opens
                                              autoFocus
                                              value={current.value}
                                              placeholder="Digite um valor…"
                                              onChange={(e) =>
                                                setTextFilter(filterConfig.columnId, {
                                                  kind: "text",
                                                  op: current.op,
                                                  value: e.target.value,
                                                })
                                              }
                                              className="w-full h-8 px-2 text-[13px] rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-white))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--comp-coral))]/30"
                                              data-testid={`header-filter-value-${header.column.id}`}
                                            />
                                          )}
                                          {headerTextActive && (
                                            <button
                                              type="button"
                                              className="text-[11.5px] text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))] underline self-start"
                                              onClick={() =>
                                                clearColumnFilter(filterConfig.columnId)
                                              }
                                              data-testid={`header-filter-clear-${header.column.id}`}
                                            >
                                              Limpar filtro
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  {filterConfig.kind === "number" &&
                                    (() => {
                                      const current: NumberFilterValue = headerNumberFilter ?? {
                                        kind: "number",
                                        op: "eq",
                                        value: null,
                                      };
                                      const currentOp =
                                        NUMBER_OPS.find((o) => o.value === current.op) ??
                                        NUMBER_OPS[0];
                                      const numberInputClass =
                                        "w-full h-8 px-2 text-[13px] rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-white))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--comp-coral))]/30 tabular-nums";
                                      return (
                                        // biome-ignore lint/a11y/noStaticElementInteractions: wrapper stops keystrokes from bubbling to dropdown
                                        <div
                                          className="flex flex-col gap-2"
                                          onKeyDown={(e) => e.stopPropagation()}
                                        >
                                          <Select
                                            value={current.op}
                                            onValueChange={(v) =>
                                              setNumberFilter(filterConfig.columnId, {
                                                kind: "number",
                                                op: v as NumberOp,
                                                value: current.value,
                                                value2: current.value2 ?? null,
                                              })
                                            }
                                          >
                                            <SelectTrigger
                                              className="h-8 text-[13px]"
                                              data-testid={`header-filter-op-${header.column.id}`}
                                            >
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {NUMBER_OPS.map((op) => (
                                                <SelectItem key={op.value} value={op.value}>
                                                  {op.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          {currentOp.needsValue >= 1 && (
                                            <input
                                              type="number"
                                              inputMode="decimal"
                                              // biome-ignore lint/a11y/noAutofocus: filter input is the primary affordance once submenu opens
                                              autoFocus
                                              value={current.value ?? ""}
                                              placeholder={
                                                currentOp.needsValue === 2 ? "De…" : "Valor"
                                              }
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                const parsed = raw === "" ? null : Number(raw);
                                                setNumberFilter(filterConfig.columnId, {
                                                  kind: "number",
                                                  op: current.op,
                                                  value: Number.isFinite(parsed as number)
                                                    ? (parsed as number)
                                                    : null,
                                                  value2: current.value2 ?? null,
                                                });
                                              }}
                                              className={numberInputClass}
                                              data-testid={`header-filter-value-${header.column.id}`}
                                            />
                                          )}
                                          {currentOp.needsValue === 2 && (
                                            <input
                                              type="number"
                                              inputMode="decimal"
                                              value={current.value2 ?? ""}
                                              placeholder="Até…"
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                const parsed = raw === "" ? null : Number(raw);
                                                setNumberFilter(filterConfig.columnId, {
                                                  kind: "number",
                                                  op: current.op,
                                                  value: current.value,
                                                  value2: Number.isFinite(parsed as number)
                                                    ? (parsed as number)
                                                    : null,
                                                });
                                              }}
                                              className={numberInputClass}
                                            />
                                          )}
                                          {headerNumberActive && (
                                            <button
                                              type="button"
                                              className="text-[11.5px] text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))] underline self-start"
                                              onClick={() =>
                                                clearColumnFilter(filterConfig.columnId)
                                              }
                                              data-testid={`header-filter-clear-${header.column.id}`}
                                            >
                                              Limpar filtro
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  {filterConfig.kind === "date" &&
                                    (() => {
                                      const current: DateFilterValue = headerDateFilter ?? {
                                        kind: "date",
                                        op: "on",
                                        value: null,
                                      };
                                      const currentOp =
                                        DATE_OPS.find((o) => o.value === current.op) ?? DATE_OPS[0];
                                      const dateInputClass =
                                        "w-full h-8 px-2 text-[13px] rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-white))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--comp-coral))]/30 tabular-nums";
                                      return (
                                        // biome-ignore lint/a11y/noStaticElementInteractions: wrapper stops keystrokes from bubbling to dropdown
                                        <div
                                          className="flex flex-col gap-2"
                                          onKeyDown={(e) => e.stopPropagation()}
                                        >
                                          <Select
                                            value={current.op}
                                            onValueChange={(v) =>
                                              setDateFilter(filterConfig.columnId, {
                                                kind: "date",
                                                op: v as DateOp,
                                                value: current.value,
                                                value2: current.value2 ?? null,
                                              })
                                            }
                                          >
                                            <SelectTrigger
                                              className="h-8 text-[13px]"
                                              data-testid={`header-filter-op-${header.column.id}`}
                                            >
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {DATE_OPS.map((op) => (
                                                <SelectItem key={op.value} value={op.value}>
                                                  {op.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                          {currentOp.needsValue >= 1 && (
                                            <input
                                              type="date"
                                              value={current.value ?? ""}
                                              onChange={(e) =>
                                                setDateFilter(filterConfig.columnId, {
                                                  kind: "date",
                                                  op: current.op,
                                                  value: e.target.value || null,
                                                  value2: current.value2 ?? null,
                                                })
                                              }
                                              className={dateInputClass}
                                              data-testid={`header-filter-value-${header.column.id}`}
                                            />
                                          )}
                                          {currentOp.needsValue === 2 && (
                                            <input
                                              type="date"
                                              value={current.value2 ?? ""}
                                              onChange={(e) =>
                                                setDateFilter(filterConfig.columnId, {
                                                  kind: "date",
                                                  op: current.op,
                                                  value: current.value,
                                                  value2: e.target.value || null,
                                                })
                                              }
                                              className={dateInputClass}
                                            />
                                          )}
                                          {headerDateActive && (
                                            <button
                                              type="button"
                                              className="text-[11.5px] text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))] underline self-start"
                                              onClick={() =>
                                                clearColumnFilter(filterConfig.columnId)
                                              }
                                              data-testid={`header-filter-clear-${header.column.id}`}
                                            >
                                              Limpar filtro
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })()}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            )}
                            {(canHide || hasCustomWidth) && <DropdownMenuSeparator />}
                            {hasCustomWidth && (
                              <DropdownMenuItem
                                onClick={() =>
                                  setColumnSizing((prev) => {
                                    const { [header.column.id]: _omit, ...rest } = prev;
                                    return rest;
                                  })
                                }
                                data-testid={`header-reset-width-${header.column.id}`}
                              >
                                Resetar largura
                              </DropdownMenuItem>
                            )}
                            {canHide && (
                              <DropdownMenuItem
                                onClick={() => header.column.toggleVisibility(false)}
                                data-testid={`header-hide-${header.column.id}`}
                              >
                                Ocultar coluna
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setColumnManagerOpen(true)}
                              data-testid={`header-manage-columns-${header.column.id}`}
                            >
                              Gerenciar colunas…
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : header.isPlaceholder ? null : (
                        labelNode
                      )}
                      {/* Resize handle */}
                      {header.column.getCanResize() && (
                        // biome-ignore lint/a11y/noStaticElementInteractions: column resize handle uses mouse/touch-only interaction
                        <div
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            header.getResizeHandler()(e);
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            header.getResizeHandler()(e);
                          }}
                          className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none hover:bg-[hsl(var(--comp-coral))]/30 ${
                            header.column.getIsResizing() ? "bg-[hsl(var(--comp-coral))]/50" : ""
                          }`}
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Skeleton rows (DESIGN v7 §7 — não usar spinner)
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                  key={`skeleton-${i}`}
                  className="border-b border-[hsl(var(--sand-divider-2))] hover:bg-transparent"
                >
                  {columns.map((_c, j) => (
                    <TableCell
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholder
                      key={`skel-${i}-${j}`}
                      className="px-5 py-3"
                      style={{ height: rowHeight }}
                    >
                      <div className="h-3 rounded bg-[hsl(var(--sand-step))] animate-pulse" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-0">
                  {activeFilterCount > 0 || (globalFilter?.length ?? 0) > 0 ? (
                    // Empty-with-filters (DESIGN v7 §11 Voz)
                    <div className="py-16 text-center">
                      <p className="text-sm text-[hsl(var(--ink-muted))]">
                        Nenhum colaborador bate com esses filtros.{" "}
                        <button
                          type="button"
                          className="underline text-[hsl(var(--ink-primary))]"
                          onClick={() => {
                            clearAllFilters();
                            setGlobalFilter("");
                          }}
                        >
                          limpar filtros
                        </button>
                      </p>
                    </div>
                  ) : (
                    // Empty-as-Tutorial (DESIGN v7 §7)
                    <div className="atmospheric-wash py-20 px-8 text-center flex flex-col items-center gap-6">
                      <h2 className="display-section text-[hsl(var(--ink-primary))]">
                        Cadastrar primeiro colaborador.
                      </h2>
                      <p className="text-sm text-[hsl(var(--ink-muted))] max-w-md">
                        A tabela de colaboradores é o ponto de entrada para compensação, performance
                        e onboarding.
                      </p>
                      <Button
                        onClick={() => setAddEmployeeOpen(true)}
                        data-testid="button-empty-add-employee"
                      >
                        Cadastrar colaborador
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                return (
                  <TableRow
                    key={row.id}
                    className="group/row border-b border-[hsl(var(--sand-divider-2))] transition-colors hover:bg-[hsl(var(--sand-step))]"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isPinned = cell.column.id === "fullName";
                      const isEditing =
                        editingCell?.rowId === row.original.id &&
                        editingCell?.columnId === cell.column.id;
                      const isEditable = isColumnEditable(cell.column.id);

                      const cellBgClass =
                        "bg-[hsl(var(--paper-white))] group-hover/row:bg-[hsl(var(--sand-step))]";

                      return (
                        <TableCell
                          key={cell.id}
                          className={`relative text-[13px] text-[hsl(var(--ink-primary))] px-3.5 whitespace-nowrap overflow-hidden transition-colors ${
                            density === "compact"
                              ? "py-1.5"
                              : density === "relaxed"
                                ? "py-4"
                                : "py-3"
                          } ${isPinned ? `sticky left-0 z-10 ${cellBgClass}` : ""} ${
                            isEditable && !isEditing ? "cursor-pointer" : ""
                          }`}
                          style={{
                            width: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                            height: rowHeight,
                          }}
                          onClick={() => {
                            lastClickedCellRef.current = {
                              rowId: row.original.id,
                              columnId: cell.column.id,
                            };
                            if (cell.column.id === "currentSalary") {
                              toast({
                                description:
                                  "Salário não é editado aqui. Abra o colaborador na aba Compensação.",
                              });
                              return;
                            }
                            if (isEditable && !isEditing) {
                              setEditingCell({
                                rowId: row.original.id,
                                columnId: cell.column.id,
                              });
                            }
                          }}
                          onDoubleClick={() => {
                            if (isEditable && !isEditing && cell.column.id !== "currentSalary") {
                              setEditingCell({
                                rowId: row.original.id,
                                columnId: cell.column.id,
                              });
                            }
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Employee Dialog — glass modal */}
      <Dialog
        open={addEmployeeOpen}
        onOpenChange={(open) => {
          setAddEmployeeOpen(open);
          if (!open)
            setNewEmployee({ fullName: "", corporateEmail: "", slackId: "", directManagerId: "" });
        }}
      >
        <DialogContent className="glass-modal" overlayClassName="glass-scrim">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>Cadastrar colaborador</DialogTitle>
              <span className="caption-mono text-[hsl(var(--ink-muted))]">Esc para fechar</span>
            </div>
            <DialogDescription>Preencha os dados do novo colaborador.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="fullName" className="caption-mono">
                Nome completo
              </Label>
              <Input
                id="fullName"
                data-testid="input-employee-name"
                value={newEmployee.fullName}
                onChange={(e) => setNewEmployee((prev) => ({ ...prev, fullName: e.target.value }))}
                placeholder="Nome do colaborador"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="corporateEmail" className="caption-mono">
                E-mail corporativo
              </Label>
              <Input
                id="corporateEmail"
                data-testid="input-employee-email"
                type="email"
                value={newEmployee.corporateEmail}
                onChange={(e) =>
                  setNewEmployee((prev) => ({ ...prev, corporateEmail: e.target.value }))
                }
                placeholder="nome@empresa.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="slackId" className="caption-mono">
                Slack ID
              </Label>
              <Input
                id="slackId"
                data-testid="input-employee-slack-id"
                value={newEmployee.slackId}
                onChange={(e) => setNewEmployee((prev) => ({ ...prev, slackId: e.target.value }))}
                placeholder="U0XXXXXXXX"
              />
            </div>
            {isSuperAdmin && (
              <div className="space-y-1.5">
                <Label className="caption-mono">Gestor direto</Label>
                <SearchableSelect
                  value={newEmployee.directManagerId}
                  onValueChange={(value) =>
                    setNewEmployee((prev) => ({ ...prev, directManagerId: value }))
                  }
                  options={employees
                    .filter((e) => e.status === "active")
                    .map((e) => ({ value: e.id, label: e.fullName }))}
                  placeholder="Selecione o gestor"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddEmployeeOpen(false)}>
              Fechar
            </Button>
            <Button
              data-testid="button-confirm-add-employee"
              onClick={() =>
                createEmployeeMutation.mutate({
                  ...newEmployee,
                  employmentType: "PJ",
                  status: "active",
                })
              }
              disabled={
                !newEmployee.fullName.trim() ||
                !newEmployee.corporateEmail.trim() ||
                !newEmployee.slackId.trim() ||
                (isSuperAdmin && !newEmployee.directManagerId) ||
                createEmployeeMutation.isPending
              }
            >
              {createEmployeeMutation.isPending ? "Cadastrando…" : "Cadastrar colaborador"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slack Sync Modal */}
      <SlackSyncModal open={slackSyncOpen} onOpenChange={setSlackSyncOpen} />

      {/* Data Upload Dialog */}
      <DataUploadDialog open={dataUploadOpen} onOpenChange={setDataUploadOpen} />

      {/* Emergency Contact Dialog */}
      {selectedEmployeeForContact && (
        <EmergencyContactDialog
          open={emergencyContactOpen}
          onOpenChange={setEmergencyContactOpen}
          employeeId={selectedEmployeeForContact.id}
          initialData={selectedEmployeeForContact.emergencyContact as any}
        />
      )}

      {/* Delete Confirmation — destructive dialog (DESIGN v7 §11) */}
      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          setDeleteConfirmOpen(open);
          if (!open) setEmployeeToDelete(null);
        }}
      >
        <DialogContent className="glass-modal" overlayClassName="glass-scrim">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>Remover colaborador {employeeToDelete?.fullName}?</DialogTitle>
              <span className="caption-mono text-[hsl(var(--ink-muted))]">Esc para fechar</span>
            </div>
            <DialogDescription>
              Esta ação é irreversível. Histórico de compensação, ciclos de performance, respostas
              de pulse survey, planos de onboarding e feedbacks de entrevista deste colaborador
              serão arquivados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setEmployeeToDelete(null);
              }}
              data-testid="button-cancel-delete"
            >
              Fechar
            </Button>
            <Button
              variant="destructive"
              data-testid="button-confirm-delete-employee"
              onClick={() =>
                employeeToDelete && deleteEmployeeMutation.mutate({ id: employeeToDelete.id })
              }
              disabled={deleteEmployeeMutation.isPending}
            >
              {deleteEmployeeMutation.isPending ? "Removendo…" : "Remover colaborador"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
